import raw from './strings.json';

/**
 * Accès typé aux chaînes du handoff.
 *
 * Choix volontaire : pas de résolution par chemin en chaîne (`t('common.title')`).
 * Les feuilles sont passées par référence — `t(S.common.title)` — donc une clé
 * qui disparaît du JSON casse la compilation au lieu d'afficher `common.title`
 * en production. C'est aussi ce qui permet de vérifier à la compilation que
 * chaque feuille possède bien ses deux langues.
 */

export const LOCALES = ['fr', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Feuille bilingue simple. */
export interface Leaf {
  readonly fr: string;
  readonly en: string;
}

/** Feuille bilingue contenant une liste (variantes de journal, libellés de pads). */
export interface ListLeaf {
  readonly fr: readonly string[];
  readonly en: readonly string[];
}

export const S = raw;

/** Vrai si la valeur est un code de langue géré. */
export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Résout une feuille dans la langue demandée, avec interpolation de `{clé}`. */
export function t(leaf: Leaf, lang: Locale, params?: Readonly<Record<string, string | number>>): string {
  const value = leaf[lang];
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (match, key: string) => {
    const replacement = params[key];
    return replacement === undefined ? match : String(replacement);
  });
}

/** Résout une liste de chaînes dans la langue demandée. */
export function tList(leaf: ListLeaf, lang: Locale): readonly string[] {
  return leaf[lang];
}

/**
 * Feuille bilingue contenant une liste de paires.
 *
 * Le skin search en est plein — recherches associées, liens de site, noms de
 * fichiers, entités voisines — et chaque entrée y est un couple « libellé,
 * précision ». Les typer ici évite un `as unknown as` par usage.
 */
export interface PairLeaf {
  readonly fr: readonly (readonly string[])[];
  readonly en: readonly (readonly string[])[];
}

/** Résout une liste de paires dans la langue demandée. */
export function tPairs(leaf: PairLeaf, lang: Locale): readonly (readonly string[])[] {
  return leaf[lang];
}

/** La FAQ est stockée à plat (`q_fr`/`a_fr`/…) : on la remet en forme ici. */
export function faq(lang: Locale): readonly { question: string; answer: string }[] {
  return S.faq.map((item) =>
    lang === 'fr'
      ? { question: item.q_fr, answer: item.a_fr }
      : { question: item.q_en, answer: item.a_en },
  );
}

/** Les trois entrées de livre d'or présentes au démarrage. */
export function guestbookSeed(lang: Locale): readonly { who: string; text: string }[] {
  return S.guestbook.seed.map((entry) => ({ who: entry.who, text: entry[lang] }));
}

/**
 * Prix : « 12 € » en français, « €12 » en anglais (cf. handoff § Formats).
 * `Intl` s'en charge et gère l'espace insécable attendu en français.
 */
export function formatPrice(amount: number, lang: Locale): string {
  try {
    return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-IE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return lang === 'fr' ? `${String(amount)} €` : `€${String(amount)}`;
  }
}

/**
 * Horloge de la barre de menu : 24 h en français, 12 h en anglais
 * (cf. handoff § Formats).
 */
export function formatClock(date: Date, lang: Locale): string {
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  if (lang === 'fr') return `${String(hours).padStart(2, '0')}:${minutes}`;
  const suffix = hours < 12 ? 'am' : 'pm';
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(twelve)}:${minutes} ${suffix}`;
}
